import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  convertPublishedCatalogImage,
  deleteProduct,
  fetchCatalogSnapshot,
  saveProduct,
  uploadCatalogImage,
} from "../lib/catalogApi";
import { isVectorAssetURL } from "../lib/catalogImages";
import type { CardProduct, CardType, Institution, Network } from "../lib/catalogTypes";
import { defaultGradientJson, parseGradientColors, tiersForNetwork } from "../lib/types";

function tierLabel(tier: string | null | undefined, networkSlug?: string | null): string {
  if (!tier || tier === "none") return "None";
  const option = tiersForNetwork(networkSlug).find((t) => t.value === tier);
  return option?.label ?? tier;
}

/** Filename-safe slug for Phase 2 pack names — lowercase, hyphens, `[a-z0-9-]`. */
export function slugifyProductName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parseWalletMatchNames(json: string | null | undefined): string[] {
  if (!json?.trim()) return [];
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

function encodeWalletMatchNames(names: string[]): string | null {
  const cleaned = names.map((n) => n.trim()).filter(Boolean);
  return cleaned.length > 0 ? JSON.stringify(cleaned) : null;
}

function productMissingArt(row: CardProduct): boolean {
  return !row.cardImageURL || isVectorAssetURL(row.cardImageURL);
}

type VerifiedFilter = "all" | "yes" | "no";
type ArtFilter = "all" | "missing";
type ActiveFilter = "all" | "active" | "inactive";

export function ProductsPage() {
  const [rows, setRows] = useState<CardProduct[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<CardProduct> | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [walletAliases, setWalletAliases] = useState<string[]>([]);
  const [aliasDraft, setAliasDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [query, setQuery] = useState("");
  const [filterInstitution, setFilterInstitution] = useState("all");
  const [filterNetwork, setFilterNetwork] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterVerified, setFilterVerified] = useState<VerifiedFilter>("all");
  const [filterArt, setFilterArt] = useState<ArtFilter>("all");
  const [filterActive, setFilterActive] = useState<ActiveFilter>("active");

  const reload = useCallback(async () => {
    const snap = await fetchCatalogSnapshot();
    setRows(snap.products);
    setInstitutions(snap.institutions);
    setNetworks(snap.networks);
    setCardTypes(snap.cardTypes);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  const institutionBySlug = useMemo(
    () => new Map(institutions.map((b) => [b.slug.toLowerCase(), b])),
    [institutions],
  );
  const networkBySlug = useMemo(
    () => new Map(networks.map((n) => [n.slug.toLowerCase(), n])),
    [networks],
  );
  const cardTypeBySlug = useMemo(
    () => new Map(cardTypes.map((t) => [t.slug.toLowerCase(), t])),
    [cardTypes],
  );

  const selectedNetwork = editing?.networkSlug
    ? networkBySlug.get(editing.networkSlug.toLowerCase()) ?? null
    : null;

  const tierOptions = useMemo(() => {
    const options = tiersForNetwork(editing?.networkSlug);
    const current = editing?.tier;
    if (current && !options.some((t) => t.value === current)) {
      return [{ value: current, label: `${current} (current)` }, ...options];
    }
    return options;
  }, [editing?.networkSlug, editing?.tier]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterInstitution !== "all" && row.institutionSlug !== filterInstitution) return false;
      if (filterNetwork !== "all" && row.networkSlug !== filterNetwork) return false;
      if (filterType !== "all" && row.cardTypeSlug !== filterType) return false;
      if (filterVerified === "yes" && !row.isVerified) return false;
      if (filterVerified === "no" && row.isVerified) return false;
      if (filterArt === "missing" && !productMissingArt(row)) return false;
      if (filterActive === "active" && !row.isActive) return false;
      if (filterActive === "inactive" && row.isActive) return false;
      if (!q) return true;
      const institutionName =
        institutionBySlug.get((row.institutionSlug ?? "").toLowerCase())?.name ?? "";
      const networkName = networkBySlug.get((row.networkSlug ?? "").toLowerCase())?.name ?? "";
      return [row.productName, row.productSlug, row.institutionSlug, row.networkSlug, institutionName, networkName]
        .some((value) => value?.toLowerCase().includes(q));
    });
  }, [
    rows,
    query,
    filterInstitution,
    filterNetwork,
    filterType,
    filterVerified,
    filterArt,
    filterActive,
    institutionBySlug,
    networkBySlug,
  ]);

  function openCreate() {
    const networkSlug = networks[0]?.slug ?? "visa";
    setSlugTouched(false);
    setWalletAliases([]);
    setAliasDraft("");
    setEditing({
      productName: "",
      productSlug: "",
      institutionSlug: institutions[0]?.slug ?? "",
      networkSlug,
      cardTypeSlug: cardTypes[0]?.slug ?? "credit",
      tier: "none",
      gradientJSON: defaultGradientJson(),
      logoURL: networks[0]?.logoURL ?? null,
      walletMatchNamesJSON: null,
      sourcePageURL: null,
      notes: null,
      isVerified: false,
      isActive: true,
      sortOrder: 0,
    });
  }

  function openEdit(row: CardProduct) {
    setSlugTouched(true);
    setWalletAliases(parseWalletMatchNames(row.walletMatchNamesJSON));
    setAliasDraft("");
    const suggested = row.productSlug?.trim() || slugifyProductName(row.productName);
    setEditing({
      ...row,
      productSlug: suggested,
      logoURL: networkBySlug.get((row.networkSlug ?? "").toLowerCase())?.logoURL ?? row.logoURL,
    });
  }

  function closeEditor() {
    setEditing(null);
    setSlugTouched(false);
    setWalletAliases([]);
    setAliasDraft("");
  }

  function onNetworkChange(slug: string) {
    if (!editing) return;
    const tiers = tiersForNetwork(slug);
    const keepTier = editing.tier && tiers.some((t) => t.value === editing.tier);
    setEditing({
      ...editing,
      networkSlug: slug,
      tier: keepTier ? editing.tier : "none",
      logoURL: networkBySlug.get(slug.toLowerCase())?.logoURL ?? null,
    });
  }

  function onProductNameChange(productName: string) {
    if (!editing) return;
    setEditing({
      ...editing,
      productName,
      productSlug: slugTouched ? editing.productSlug : slugifyProductName(productName),
    });
  }

  function addAlias(raw: string) {
    const value = raw.trim();
    if (!value) return;
    setWalletAliases((prev) => (prev.some((a) => a.toLowerCase() === value.toLowerCase()) ? prev : [...prev, value]));
    setAliasDraft("");
  }

  function onAliasKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addAlias(aliasDraft);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.productName) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const productSlug =
        (editing.productSlug ?? "").trim() || slugifyProductName(editing.productName);
      await saveProduct({
        recordName: editing.recordName,
        institutionSlug: editing.institutionSlug,
        networkSlug: editing.networkSlug,
        cardTypeSlug: editing.cardTypeSlug,
        productName: editing.productName,
        productSlug,
        tier: editing.tier || "none",
        gradientJSON: editing.gradientJSON ?? defaultGradientJson(),
        cardImageURL: editing.cardImageURL ?? null,
        logoURL: selectedNetwork?.logoURL ?? null,
        walletMatchNamesJSON: encodeWalletMatchNames(walletAliases),
        sourcePageURL: editing.sourcePageURL?.trim() || null,
        notes: editing.notes?.trim() || null,
        isVerified: Boolean(editing.isVerified),
        isActive: editing.isActive !== false,
        sortOrder: Number(editing.sortOrder ?? 0),
      });
      closeEditor();
      await reload();
      setMessage("Card saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function replaceImage(file: File | null) {
    if (!file || !editing) return;
    setUploading(true);
    setError(null);
    try {
      const cardImageURL = await uploadCatalogImage(file, "card");
      setEditing((prev) => (prev ? { ...prev, cardImageURL } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function removeImageFromEditor() {
    if (!editing?.cardImageURL) return;
    if (!window.confirm("Remove the card face art from this product?")) return;
    setEditing({ ...editing, cardImageURL: null });
  }

  async function convertEditorImage() {
    if (!editing?.cardImageURL || !isVectorAssetURL(editing.cardImageURL)) return;
    setUploading(true);
    setError(null);
    try {
      const cardImageURL = await convertPublishedCatalogImage(editing.cardImageURL, "card");
      setEditing((prev) => (prev ? { ...prev, cardImageURL } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : "SVG conversion failed");
    } finally {
      setUploading(false);
    }
  }

  async function quickReplaceRow(row: CardProduct, file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const cardImageURL = await uploadCatalogImage(file, "card");
      await saveProduct({ ...row, cardImageURL });
      setMessage(`Updated art for ${row.productName}.`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  const gradient = parseGradientColors(editing?.gradientJSON ?? defaultGradientJson());
  const isCreating = Boolean(editing && !editing.recordName);

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Cards</h1>
            <p>
              Products = institution + network + type + face art + Wallet match aliases. Artwork
              stays on R2; aliases are cosmetic display names only (no last-4).
            </p>
          </div>
          <div className="row-actions">
            <button className="btn btn-secondary" type="button" onClick={() => void reload()}>
              <Icon name="refresh" size={15} />
              Refresh
            </button>
            <button className="btn btn-primary" type="button" onClick={openCreate}>
              <Icon name="plus" size={15} />
              Add product
            </button>
          </div>
        </div>

        {error && <div className="banner error">{error}</div>}
        {message && <div className="banner success">{message}</div>}

        <section className="panel cards-toolbar">
          <div className="cards-filters">
            <div className="field search">
              <input
                type="search"
                placeholder="Search name, institution, network…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <label>
              Institution
              <select value={filterInstitution} onChange={(e) => setFilterInstitution(e.target.value)}>
                <option value="all">All</option>
                {institutions.map((b) => (
                  <option key={b.recordName} value={b.slug}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Network
              <select value={filterNetwork} onChange={(e) => setFilterNetwork(e.target.value)}>
                <option value="all">All</option>
                {networks.map((n) => (
                  <option key={n.recordName} value={n.slug}>
                    {n.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Type
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">All</option>
                {cardTypes.map((t) => (
                  <option key={t.recordName} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Verified
              <select
                value={filterVerified}
                onChange={(e) => setFilterVerified(e.target.value as VerifiedFilter)}
              >
                <option value="all">All</option>
                <option value="yes">Verified</option>
                <option value="no">Unverified</option>
              </select>
            </label>
            <label>
              Art
              <select value={filterArt} onChange={(e) => setFilterArt(e.target.value as ArtFilter)}>
                <option value="all">All</option>
                <option value="missing">Missing art</option>
              </select>
            </label>
            <label>
              Status
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value as ActiveFilter)}
              >
                <option value="active">Active only</option>
                <option value="all">All</option>
                <option value="inactive">Inactive only</option>
              </select>
            </label>
          </div>
          <p className="field-hint" style={{ margin: "0.35rem 0 0" }}>
            Showing {filteredRows.length} of {rows.length}. Default filter hides inactive cards.
          </p>
        </section>

        <div className="panel table-wrap">
          <table className="cards-table">
            <thead>
              <tr>
                <th>Face</th>
                <th>Product</th>
                <th>Institution</th>
                <th>Network</th>
                <th>Type</th>
                <th>Tier</th>
                <th>Badges</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const institution =
                  institutionBySlug.get((row.institutionSlug ?? "").toLowerCase())?.name ??
                  row.institutionSlug ??
                  "—";
                const network =
                  networkBySlug.get((row.networkSlug ?? "").toLowerCase())?.name ??
                  row.networkSlug ??
                  "—";
                const type =
                  cardTypeBySlug.get((row.cardTypeSlug ?? "").toLowerCase())?.name ??
                  row.cardTypeSlug ??
                  "—";
                const missingArt = productMissingArt(row);
                return (
                  <tr key={row.recordName} className={row.isActive ? undefined : "row-inactive"}>
                    <td>
                      <div className="card-face-thumb">
                        {row.cardImageURL && !isVectorAssetURL(row.cardImageURL) ? (
                          <img src={row.cardImageURL} alt="" />
                        ) : (
                          <span className="placeholder">{missingArt ? "No art" : "SVG"}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{row.productName}</strong>
                      {row.productSlug ? (
                        <div className="muted" style={{ fontSize: "0.78rem" }}>
                          {row.productSlug}
                        </div>
                      ) : null}
                    </td>
                    <td>{institution}</td>
                    <td>{network}</td>
                    <td>{type}</td>
                    <td>{tierLabel(row.tier, row.networkSlug)}</td>
                    <td className="badge-stack">
                      {row.isVerified && <span className="badge ok">Verified</span>}
                      {missingArt && <span className="badge warn">Missing art</span>}
                      {!row.isActive && <span className="badge bad">Inactive</span>}
                    </td>
                    <td className="row-actions">
                      <button className="btn btn-secondary btn-sm" type="button" onClick={() => openEdit(row)}>
                        Edit
                      </button>
                      <label className="btn btn-brass btn-sm">
                        Art
                        <input
                          type="file"
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          hidden
                          disabled={busy}
                          onChange={(e) => void quickReplaceRow(row, e.target.files?.[0] ?? null)}
                        />
                      </label>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        onClick={() =>
                          void deleteProduct(row.recordName)
                            .then(reload)
                            .catch((err) =>
                              setError(err instanceof Error ? err.message : "Delete failed"),
                            )
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredRows.length === 0 && (
            <div className="empty" style={{ padding: "2rem 1rem" }}>
              <strong>No cards match these filters</strong>
            </div>
          )}
        </div>
      </main>

      {editing && (
        <div className="drawer-backdrop" onClick={closeEditor} role="presentation">
          <aside
            className="drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="card-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-head">
              <div>
                <span className="overline">Cards</span>
                <h2 id="card-drawer-title">{isCreating ? "Add product" : "Edit product"}</h2>
              </div>
              <button className="btn btn-quiet btn-sm" type="button" onClick={closeEditor}>
                Close
              </button>
            </div>

            <form className="drawer-body" onSubmit={(e) => void onSubmit(e)}>
              <section className="form-section">
                <h3>Identity</h3>
                <p className="field-hint">Name and stable slug used later for pack filenames.</p>
                <div className="form-grid">
                  <label className="full">
                    Product name
                    <input
                      value={editing.productName ?? ""}
                      onChange={(e) => onProductNameChange(e.target.value)}
                      required
                    />
                  </label>
                  <label className="full">
                    Product slug
                    <input
                      value={editing.productSlug ?? ""}
                      onChange={(e) => {
                        setSlugTouched(true);
                        setEditing({ ...editing, productSlug: slugifyProductName(e.target.value) });
                      }}
                      placeholder="auto-from-name"
                    />
                    <span className="field-hint">
                      Lowercase hyphens only. Auto-fills from the name until you edit it.
                    </span>
                  </label>
                  <label className="full">
                    Source page URL
                    <input
                      type="url"
                      value={editing.sourcePageURL ?? ""}
                      onChange={(e) =>
                        setEditing({ ...editing, sourcePageURL: e.target.value || null })
                      }
                      placeholder="https://…"
                    />
                  </label>
                  <label className="full">
                    Notes
                    <textarea
                      rows={2}
                      value={editing.notes ?? ""}
                      onChange={(e) => setEditing({ ...editing, notes: e.target.value || null })}
                      placeholder="Short admin notes"
                    />
                  </label>
                </div>
              </section>

              <section className="form-section">
                <h3>Classification</h3>
                <p className="field-hint">Institution, network, type, and tier.</p>
                <div className="form-grid">
                  <label>
                    Institution
                    <select
                      value={editing.institutionSlug ?? ""}
                      onChange={(e) => setEditing({ ...editing, institutionSlug: e.target.value })}
                    >
                      {institutions.map((b) => (
                        <option key={b.recordName} value={b.slug}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Network
                    <select
                      value={editing.networkSlug ?? ""}
                      onChange={(e) => onNetworkChange(e.target.value)}
                    >
                      {networks.map((n) => (
                        <option key={n.recordName} value={n.slug}>
                          {n.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Card type
                    <select
                      value={editing.cardTypeSlug ?? ""}
                      onChange={(e) => setEditing({ ...editing, cardTypeSlug: e.target.value })}
                    >
                      {cardTypes.map((t) => (
                        <option key={t.recordName} value={t.slug}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Tier
                    <select
                      value={editing.tier || "none"}
                      onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                    >
                      {tierOptions.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <span className="field-hint">None = generic / undefined.</span>
                  </label>
                </div>
              </section>

              <section className="form-section">
                <h3>Art</h3>
                <p className="field-hint">
                  Raster faces only. SVG uploads convert to PNG; large art scales to 1200px.
                </p>
                <div className="form-grid">
                  <label>
                    Gradient start
                    <input
                      type="color"
                      value={gradient[0].startsWith("#") ? gradient[0] : `#${gradient[0]}`}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          gradientJSON: JSON.stringify([e.target.value, gradient[1]]),
                        })
                      }
                    />
                  </label>
                  <label>
                    Gradient end
                    <input
                      type="color"
                      value={gradient[1].startsWith("#") ? gradient[1] : `#${gradient[1]}`}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          gradientJSON: JSON.stringify([gradient[0], e.target.value]),
                        })
                      }
                    />
                  </label>
                  <label className="full">
                    Card image URL
                    <input
                      value={editing.cardImageURL ?? ""}
                      onChange={(e) =>
                        setEditing({ ...editing, cardImageURL: e.target.value || null })
                      }
                    />
                  </label>
                  <div className="full art-preview-row">
                    <div className="card-face-thumb large">
                      {editing.cardImageURL && !isVectorAssetURL(editing.cardImageURL) ? (
                        <img src={editing.cardImageURL} alt="" />
                      ) : (
                        <span className="placeholder">Preview</span>
                      )}
                    </div>
                    <div className="row-actions">
                      <label className="btn btn-brass btn-sm">
                        {uploading ? "Uploading…" : editing.cardImageURL ? "Replace" : "Upload"}
                        <input
                          type="file"
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          hidden
                          disabled={uploading || busy}
                          onChange={(e) => void replaceImage(e.target.files?.[0] ?? null)}
                        />
                      </label>
                      {isVectorAssetURL(editing.cardImageURL) && (
                        <button
                          className="btn btn-brass btn-sm"
                          type="button"
                          disabled={uploading || busy}
                          onClick={() => void convertEditorImage()}
                        >
                          Convert to PNG
                        </button>
                      )}
                      {editing.cardImageURL && (
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          disabled={busy}
                          onClick={() => void removeImageFromEditor()}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="field full">
                    <span className="field-label">Network logo</span>
                    {selectedNetwork?.logoURL ? (
                      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                        <img
                          className="catalog-logo network-logo"
                          src={selectedNetwork.logoURL}
                          alt=""
                        />
                        <span className="field-hint">
                          Applied from {selectedNetwork.name} on save.
                        </span>
                      </div>
                    ) : (
                      <span className="field-hint">
                        {selectedNetwork
                          ? `${selectedNetwork.name} has no cached logo yet — add one on Networks.`
                          : "Select a network to inherit its logo."}
                      </span>
                    )}
                  </div>
                </div>
              </section>

              <section className="form-section">
                <h3>Matching</h3>
                <p className="field-hint">
                  Aliases for Apple Wallet / Shortcuts “Card or Pass” display names. Last-4 is not
                  available from Shortcuts. Cosmetic catalog metadata only — never PAN or account
                  numbers.
                </p>
                <div className="tag-editor">
                  <div className="tag-list">
                    {walletAliases.map((alias) => (
                      <span key={alias} className="tag-chip">
                        {alias}
                        <button
                          type="button"
                          aria-label={`Remove ${alias}`}
                          onClick={() =>
                            setWalletAliases((prev) => prev.filter((item) => item !== alias))
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    value={aliasDraft}
                    onChange={(e) => setAliasDraft(e.target.value)}
                    onKeyDown={onAliasKeyDown}
                    onBlur={() => addAlias(aliasDraft)}
                    placeholder="Add alias, press Enter"
                  />
                </div>
              </section>

              <section className="form-section">
                <h3>Status</h3>
                <div className="form-grid">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(editing.isVerified)}
                      onChange={(e) => setEditing({ ...editing, isVerified: e.target.checked })}
                    />
                    Verified
                  </label>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={editing.isActive !== false}
                      onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                  <label>
                    Sort order
                    <input
                      type="number"
                      value={editing.sortOrder ?? 0}
                      onChange={(e) =>
                        setEditing({ ...editing, sortOrder: Number(e.target.value || 0) })
                      }
                    />
                  </label>
                </div>
              </section>

              <div className="drawer-foot">
                <button className="btn btn-primary" type="submit" disabled={busy || uploading}>
                  Save
                </button>
                <button className="btn btn-secondary" type="button" onClick={closeEditor}>
                  Cancel
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </AppShell>
  );
}
